import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import pick from 'lodash/pick';
import omit from 'lodash/omit';
import mapKeys from 'lodash/mapKeys';

import {ResourceType} from 'common/widgets/property-filter-menu/models/types';
import {getSegmentType} from 'common/widgets/query-block/segment-menu/types';

import FullLengthDateRange from 'common/widgets/date-range-picker/models/full-length-date-range';
import {QueryEntry, QueryEventEntry} from 'common/widgets/query-event-selector/types';
import {EventItem} from 'common/widgets/query-block/events-menu/types';
import {PropertyItem} from 'common/widgets/query-block/properties-menu/types';

import {convertFlowsReportToFlowsRequestParams} from './converter';
import {ChartType, CHART_TYPE_TO_QUERY_PARAM} from './types';
import {DEFAULT_VALUES as FUNNELS_BOOKMARK_DEFAULT_VALUES, FunnelsBookmark} from '../funnels/bookmark';

export const FLOWS_REPORT_DEFAULTS = Object.freeze({
  alignBreakdowns: true,
  alwaysBreakdown: false,
  binaryTag: null,
  cardinality: 3,
  chartType: ChartType.Sankey,
  cohortFilters: [],
  graphSortPriority: `absolute`,
  groupBy: [],
  hiddenEvents: [],
  id: null,
  listSortPriority: `end`,
  maxResults: 50,
  maxSteps: 3,
  name: null,
  description: null,
  relativeBreakdowns: false,
  reverse: false,
  segments: [],
  timePercentiles: [],
  uniqueFlows: false,
  userId: null,
  version: `1`,
  visibleEvents: [],
  is_visibility_restricted: false,
  is_modification_restricted: false,
  can_update_basic: false,
  can_update_visibility: false,
  can_update_restricted: false,
});

export const BOOKMARK_PARAM_MAP = Object.freeze({
  hidden_events: `hiddenEvents`,
  visible_events: `visibleEvents`,
  graph_sort_priority: `graphSortPriority`,
  list_sort_priority: `listSortPriority`,
  segments: `segments`,
});

const BOOKMARK_COPY_OVER_KEYS = Object.freeze([`maxSteps`, `maxResults`, `cardinality`, `chartType`, `reverse`]);

const FLOWS_REPORT_DEFAULTS_KEYS = Object.keys(FLOWS_REPORT_DEFAULTS);
const BOOKMARK_PARAM_MAP_KEYS = Object.keys(BOOKMARK_PARAM_MAP);
const DEFAULTS = {...FLOWS_REPORT_DEFAULTS, ...FUNNELS_BOOKMARK_DEFAULT_VALUES};
const URL_PARAMS = Object.keys(DEFAULTS);

export class FlowsReport extends FunnelsBookmark {
  public static fromBookmark(bookmark) {
    const {
      params,
      name,
      id,
      user_id: userId,
      description,
      is_visibility_restricted,
      is_modification_restricted,
      can_update_basic,
      can_update_visibility,
      can_update_restricted,
    } = bookmark;
    const funnelsBookmarkParams = FunnelsBookmark.bookmarkToClassParams(params);

    const convertedParams = mapKeys(pick(params, BOOKMARK_PARAM_MAP_KEYS), (_, k) => BOOKMARK_PARAM_MAP[k]);
    const copiedParams = pick(params, BOOKMARK_COPY_OVER_KEYS);

    const attrs = {
      ...copiedParams,
      ...funnelsBookmarkParams,
      ...convertedParams,
      name,
      id,
      userId,
      description,
      is_visibility_restricted,
      is_modification_restricted,
      can_update_basic,
      can_update_visibility,
      can_update_restricted,
    };

    return new FlowsReport(attrs);
  }

  get events() {
    return this._events;
  }

  set events(events: QueryEntry[]) {
    this._events = this.generateFlowsEvents(events);
  }

  get eventSelection() {
    return this.events.filter(Boolean);
  }

  get cohortGroupBys() {
    return this.groupBy.filter((segment) => getSegmentType(segment) === ResourceType.Cohort);
  }

  get trackingParams() {
    return {
      'Anchor Events': this.eventSelection.map((entry: QueryEventEntry) => entry.event.label),
      'Custom event': this.eventSelection.some((entry: QueryEventEntry) => entry.event.custom),
      'Tab name': this.chartType,
      reverse: !!this.reverse,
      '# of filters': 0,
      '# of breakdowns': this.segments.length,
      '# of hidden events': this.hiddenEvents.length,
      '# of cohort filters': this.cohortFilters.length,
      '# of cohort segments': this.groupBy.map((segment) => getSegmentType(segment) === ResourceType.Cohort).length,
      'Query time Sampling': this.querySampling,
    };
  }
  public chartType: ChartType;
  public graphSortPriority: string;
  public listSortPriority: string;
  public hiddenEvents: string[];
  public visibleEvents: string[];
  public reverse: boolean;
  public cardinality: number;
  public maxSteps: number;
  // TODO: change timePercentiles to a boolean when Flows API is dropped
  public timePercentiles: number[];
  public maxResults: number;
  public relativeBreakdowns: boolean;
  public segments: Array<{
    event: EventItem;
    property: PropertyItem;
  }>;
  private defaults: Partial<FlowsReport>;
  private _events: QueryEntry[];

  constructor(attrs = {}) {
    super(attrs);
    Object.assign(this, FLOWS_REPORT_DEFAULTS);
    this.defaults = pick(this, Object.keys(DEFAULTS));
    Object.assign(this, pick(attrs, FLOWS_REPORT_DEFAULTS_KEYS));
  }

  /**
   * Calculate which params are non-default, and serialize any
   * complex values (like dateRange)
   */
  public toUrlData() {
    const urlParams = URL_PARAMS.reduce((params, key) => {
      if (!isEqual(this[key], this.defaults[key])) {
        params[key] = this[key];
      }
      return params;
    }, {});

    return isEmpty(urlParams)
      ? null
      : {
          ...urlParams,
          dateRange: this.dateRange.getAttributes(),
          version: this.version,
        };
  }

  public paramEquals(param, value) {
    switch (param) {
      case `dateRange`:
        return this.dateRange.equals(value);
      case `events`:
        return isEqual(this.events, this.generateFlowsEvents(value));
      default:
        return this[param] === value;
    }
  }

  public update(params: Partial<FlowsReport> = {}): boolean {
    let changed = false;

    for (const [k, v] of Object.entries(params)) {
      if (!this.paramEquals(k, v)) {
        changed = true;
        this[k] = v;
      }
    }

    if (`dateRange` in params) {
      this.dateRange = new FullLengthDateRange(params.dateRange);
    }

    // updating maxSteps requires re-generating the
    // events structure as it is derived from maxSteps
    if (`maxSteps` in params || `reverse` in params) {
      // eslint-disable-next-line no-self-assign
      this.events = this.events;
    }

    return changed;
  }

  public generateBookmark(): any {
    const includeNegatedCohort = this.cohortGroupBys.length === 1;
    const funnelsBookmark = super.generateBookmark({includeNegatedCohort});
    const bookmark = {
      ...omit(funnelsBookmark, `length_unit`, `length`),
      cardinality_threshold: this.chartType === ChartType.Sankey ? this.cardinality : this.maxResults,
      flows_merge_type: CHART_TYPE_TO_QUERY_PARAM[this.chartType],
      segments: this.segments,
    };

    if (this.visibleEvents.length) {
      bookmark[`visible_events`] = this.visibleEvents;
    } else {
      bookmark[`hidden_events`] = this.hiddenEvents;
    }

    if (this.chartType === ChartType.Sankey) {
      bookmark[`graph_sort_priority`] = this.graphSortPriority;
    } else {
      bookmark[`list_sort_priority`] = this.listSortPriority;
    }

    if (this.timePercentiles.length) {
      bookmark[`time_percentiles_enabled`] = true;
    }

    if (this.cohortGroupBys.length) {
      if (this.relativeBreakdowns) {
        bookmark[`graph_sort_priority`] = `relative`;
      }

      if (this.chartType === ChartType.Sankey) {
        bookmark[`exclude_everything_else_user_segment`] = `true`;
      }
    }

    Object.assign(bookmark, pick(this, BOOKMARK_COPY_OVER_KEYS));
    return bookmark;
  }

  public paramsForQuery() {
    return {
      ...convertFlowsReportToFlowsRequestParams(this),
      ...omit(this.generateBookmark(), [
        ...BOOKMARK_COPY_OVER_KEYS,
        `group_by`,
        `exclusions`,
        `filters`,
        `version`,
        `count_type`,
        `steps`,
        `date_range`,
        `time_percentiles_enabled`,
        `filter_by_cohort`,
        `filter_by_event`,
        `segments`,
      ]),
      segments: this.segmentParamsForQuery(),
    };
  }

  private segmentParamsForQuery() {
    return this.segments.map((seg) => ({
      event: seg.event.value,
      property: seg.property.value,
      label: seg.property.label,
    }));
  }

  /**
   * Generates events with forward and reverse fields
   */
  private generateFlowsEvents(events: QueryEntry[]): QueryEntry[] {
    const maxSteps = this.chartType === ChartType.Paths ? 5 : this.maxSteps || FLOWS_REPORT_DEFAULTS.maxSteps;
    // handle non-beta single event anchorSelection
    if (events.length === 1 && events[0]) {
      const entry = omit(events[0], `forward`, `reverse`) as QueryEntry;
      return [
        {
          ...entry,
          event: entry.event,
          [this.reverse ? `reverse` : `forward`]: maxSteps,
        },
      ];
    }

    // fill left to right direction for all anchors except last
    // allows for setting reverse flows without explicitly
    // defining the `reverse` property
    const isSparse = events.some((entry) => isEmpty(entry));

    return events.map((entry, index) => {
      if (isEmpty(entry)) {
        return null;
      }

      entry = {
        ...(omit(entry, `forward`, `reverse`) as QueryEntry),
        event: entry.event,
      };

      if (this.reverse) {
        entry[`reverse`] = maxSteps;
      } else if (index === events.length - 1) {
        entry[`reverse`] = isSparse ? maxSteps : 0;
      } else {
        entry[`forward`] = maxSteps;
      }

      return entry;
    });
  }
}
